import {
  AlertType,
  AloftPrice,
  QualityControlFieldTitle,
  ValueType,
  expected,
  getAlertTypeLabel,
  isEditableQualityControlFieldTitle,
  normalizeQualityControlFieldTitle,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { useCallback } from 'react';

import { submitEditSubjectPropertyDetailsForm } from '../../../redux/features/actions/submitEditSubjectPropertyDetailsForm.ts';
import { setCommentaryV2 } from '../../../redux/features/commentarySlice.ts';
import { setReconciliation } from '../../../redux/features/reconciliationSlice.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectPropertyIdentificationById } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import { selectSubjectPropertyType } from '../../../redux/features/selectors/selectSubjectPropertyType.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';

const ORDER_DETAIL_TITLES = new Set<QualityControlFieldTitle>([
  'Zoning',
  'Zoning Compliance',
  'Parcel Number',
  'Legal Description',
]);

const ALERT_COMMENTARY_PREFIX = 'requiredFields.alertsCommentary.';

const getOrderDetailsValue = (
  title: QualityControlFieldTitle,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationById>,
): string => {
  switch (title) {
    case 'Zoning':
      return propertyIdentification?.zoning ?? '';
    case 'Zoning Compliance':
      return propertyIdentification?.zoningCompliance ?? '';
    case 'Parcel Number':
      return propertyIdentification?.parcelNumber ?? '';
    case 'Legal Description':
      return propertyIdentification?.legalDescription ?? '';
    default:
      return '';
  }
};

const getCommentaryValue = (
  title: QualityControlFieldTitle,
  commentary: ReturnType<typeof selectCommentaryV2>,
): string => {
  switch (title) {
    case 'Market Condition Commentary':
      return commentary.marketConditionsV2?.commentary ?? '';
    case 'Prior Sales Commentary':
      return commentary.priorSales?.commentary ?? '';
    case 'AIV / ARV Reconciliation Commentary':
      return (
        commentary.reconciliation?.reconciliationCommentary ??
        commentary.reconciliation?.aivReconciliationCommentary ??
        ''
      );
    case 'Highest & Best Use Commentary':
      return commentary.reconciliation?.highestAndBestUseCommentary ?? '';
    case 'Revision / ROV Commentary':
      return commentary.reconciliation?.revisionCommentary ?? '';
    case 'Total Cost to Cure Commentary':
      return commentary.reconciliation?.totalCostToCureCommentary ?? '';
    default:
      return '';
  }
};

const orderDetailPayloadKey = (title: QualityControlFieldTitle) => {
  if (title === 'Zoning') return 'zoning';
  if (title === 'Zoning Compliance') return 'zoningCompliance';
  if (title === 'Parcel Number') return 'parcelNumber';
  return 'legalDescription';
};

export const useQualityControlField = (fieldTitle: string) => {
  const dispatch = useAppDispatch();
  const normalizedTitle = normalizeQualityControlFieldTitle(fieldTitle);
  const propertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const commentary = useAppSelector(selectCommentaryV2);
  const reconciliation = useAppSelector(selectReconciliation);
  const propertyType = useAppSelector(selectSubjectPropertyType);
  const alertCommentaryKey = fieldTitle.startsWith(ALERT_COMMENTARY_PREFIX)
    ? fieldTitle.slice(ALERT_COMMENTARY_PREFIX.length)
    : undefined;

  const isEditable = isEditableQualityControlFieldTitle(normalizedTitle);
  const qualityControlFieldTitle = isEditable ? normalizedTitle : undefined;
  const title = alertCommentaryKey
    ? `${getAlertTypeLabel(alertCommentaryKey.split('#')[0] as AlertType)} Commentary`
    : qualityControlFieldTitle;

  const value = (() => {
    if (alertCommentaryKey) return commentary.alerts?.[alertCommentaryKey] ?? '';
    if (!qualityControlFieldTitle) return '';
    if (ORDER_DETAIL_TITLES.has(qualityControlFieldTitle))
      return getOrderDetailsValue(qualityControlFieldTitle, propertyIdentification);
    if (qualityControlFieldTitle === 'As Is / Subject To')
      return reconciliation.valueTypes[0] ?? '';
    if (qualityControlFieldTitle === 'Total Cost to Cure') {
      return (
        orUndefined(schAloftPrice.parse)(reconciliation.totalCostToCure)?.toString(true, true) ?? ''
      );
    }
    return getCommentaryValue(qualityControlFieldTitle, commentary);
  })();

  const onChange = useCallback(
    (nextValue: string) => {
      if (alertCommentaryKey) {
        dispatch(
          setCommentaryV2({
            ...commentary,
            alerts: { ...commentary.alerts, [alertCommentaryKey]: nextValue },
          }),
        );
        return;
      }
      if (!qualityControlFieldTitle) return;

      if (ORDER_DETAIL_TITLES.has(qualityControlFieldTitle)) {
        dispatch(
          submitEditSubjectPropertyDetailsForm({
            propertyType,
            [orderDetailPayloadKey(qualityControlFieldTitle)]: nextValue,
          }),
        );
        return;
      }

      if (qualityControlFieldTitle === 'As Is / Subject To') {
        dispatch(
          setReconciliation({
            ...reconciliation,
            valueTypes: nextValue ? [nextValue as ValueType] : [],
          }),
        );
        return;
      }

      if (qualityControlFieldTitle === 'Total Cost to Cure') {
        dispatch(
          setReconciliation({
            ...reconciliation,
            totalCostToCure: nextValue ? AloftPrice.fromDollarLike(nextValue)?.toJSON() : undefined,
          }),
        );
        return;
      }

      if (qualityControlFieldTitle === 'Market Condition Commentary') {
        dispatch(
          setCommentaryV2({
            ...commentary,
            marketConditionsV2: {
              ...expected(commentary.marketConditionsV2),
              commentary: nextValue,
            },
          }),
        );
        return;
      }

      if (qualityControlFieldTitle === 'Prior Sales Commentary') {
        dispatch(
          setCommentaryV2({
            ...commentary,
            priorSales: {
              ...commentary.priorSales,
              commentary: nextValue,
            },
          }),
        );
        return;
      }

      dispatch(
        setCommentaryV2({
          ...commentary,
          reconciliation: {
            ...commentary.reconciliation,
            ...(qualityControlFieldTitle === 'AIV / ARV Reconciliation Commentary' && {
              aivReconciliationCommentary: nextValue,
              reconciliationCommentary: nextValue,
            }),
            ...(qualityControlFieldTitle === 'Highest & Best Use Commentary' && {
              highestAndBestUseCommentary: nextValue,
            }),
            ...(qualityControlFieldTitle === 'Revision / ROV Commentary' && {
              revisionCommentary: nextValue,
            }),
            ...(qualityControlFieldTitle === 'Total Cost to Cure Commentary' && {
              totalCostToCureCommentary: nextValue,
            }),
          },
        }),
      );
    },
    [
      alertCommentaryKey,
      commentary,
      dispatch,
      propertyType,
      qualityControlFieldTitle,
      reconciliation,
    ],
  );

  return {
    title,
    value,
    onChange,
    isEditable: Boolean(alertCommentaryKey) || isEditable,
  };
};
